import React, { useRef, useEffect, useState } from 'react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { getStatusIcon } from 'components/TasksCalendar/getStatusIcon'
import moment from 'moment'

interface Task {
	_id: string
	taskName: string
	completedBy: string | Date
	taskStatus?: string
	assignedTo?:
		| {
				_id?: string
				name?: string
				firstName?: string
				image?: string
		  }
		| string
		| null
	completed?: boolean
}

interface Props {
	tasks: Task[]
	onTaskClick?: (task: Task) => void
	/** Called when a task is dragged horizontally and dropped at a new position.
	 *  newDate is an ISO string like "YYYY-MM-DDT12:00:00.000Z". When omitted, task
	 *  drag-to-reschedule is disabled (clicking still works). */
	onTaskDateChange?: (task: Task, newDate: string) => void
}

// Minimum cursor movement (px) required for a mousedown→mouseup to be treated as
// a drag rather than a click. Below this threshold, the gesture opens the task
// sheet via onTaskClick; above it, the gesture reschedules via onTaskDateChange.
const DRAG_THRESHOLD_PX = 5

// Distance settings for labels ABOVE timeline (for staggering when overlapping)
const UPPER_ABOVE_DISTANCE = 80
const LOWER_ABOVE_DISTANCE = 160

// Line length settings for labels ABOVE timeline
const UPPER_ABOVE_LINE_LENGTH = 160
const LOWER_ABOVE_LINE_LENGTH = 80

// Distance settings for labels BELOW timeline (for staggering when overlapping)
const UPPER_BELOW_DISTANCE = 160
const LOWER_BELOW_DISTANCE = 80

// Line length settings for labels BELOW timeline
const UPPER_BELOW_LINE_LENGTH = 80
const LOWER_BELOW_LINE_LENGTH = 160

const TasksTimeline: React.FC<Props> = ({ tasks, onTaskClick, onTaskDateChange }) => {
	const scrollContainerRef = useRef<HTMLDivElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const [scrollLeft, setScrollLeft] = useState(0)
	const [scrollWidth, setScrollWidth] = useState(0)
	const [clientWidth, setClientWidth] = useState(0)
	const [containerWidth, setContainerWidth] = useState(0)
	const [isInitialized, setIsInitialized] = useState(false)
	const [resolution, setResolution] = useState<'1 Day' | '1 Week' | '2 Weeks'>('1 Week')

	// Pan state: while non-null, mouse is held down on the timeline whitespace and
	// the user is dragging horizontally to scroll back/forward in time.
	const [panState, setPanState] = useState<{ startX: number; startScrollLeft: number } | null>(null)

	// Task drag state: while non-null, the user is mid-drag on a single task. After
	// the threshold is crossed, hasMoved flips to true and we render the task at the
	// drifted position. On mouseup: if hasMoved → emit onTaskDateChange and set
	// dropTargetISO (pin the task there visually until the parent's data update arrives).
	const [taskDrag, setTaskDrag] = useState<{
		task: Task
		startX: number
		startPosition: number
		currentDeltaX: number
		hasMoved: boolean
		// Set after mouseup: keeps the task pinned at the dropped pixel position until
		// the parent re-renders with the new dateDue matching this date (YYYY-MM-DD).
		dropTargetDay?: string
	} | null>(null)

	// Ensure tasks is always an array
	const tasksArray = Array.isArray(tasks) ? tasks : []

	// Filter and sort tasks by due date (completedBy)
	// Include all tasks that have a completedBy date
	const sortedTasks = tasksArray
		.filter(task => task && task.completedBy && moment(task.completedBy).isValid())
		.sort((a, b) => {
			const dateA = moment(a.completedBy).valueOf()
			const dateB = moment(b.completedBy).valueOf()
			return dateA - dateB
		})

	// Calculate timeline dimensions
	// If no tasks, show a default range
	const now = moment()
	const minDate = sortedTasks.length > 0 ? moment(sortedTasks[0].completedBy) : moment().subtract(30, 'days')
	const maxDate =
		sortedTasks.length > 0 ? moment(sortedTasks[sortedTasks.length - 1].completedBy) : moment().add(30, 'days')

	// Ensure we have at least some range
	if (minDate.isSameOrAfter(maxDate)) {
		maxDate.add(30, 'days')
	}

	// Ensure the timeline range includes today
	const minDateWithToday = moment.min(minDate, now.startOf('day'))
	const maxDateWithToday = moment.max(maxDate, now.startOf('day'))

	// Pad both ends so the user can pan past the last/first scheduled task to plan
	// future work or view earlier history. Clone first so we don't mutate `now`.
	const PAN_BUFFER_DAYS = 180
	const finalMinDate = moment(minDateWithToday).subtract(PAN_BUFFER_DAYS, 'days')
	const finalMaxDate = moment(maxDateWithToday).add(PAN_BUFFER_DAYS, 'days')

	const dateRange = Math.max(1, finalMaxDate.diff(finalMinDate, 'days'))

	// Fixed viewport width - this should never change once initialized
	// Account for padding: 50px left + 50px right = 100px
	const viewportWidth = isInitialized && containerWidth > 0 ? containerWidth - 100 : 800

	// Calculate resolution factor to adjust timeline scale
	// Lower resolution (2 Weeks) = shorter timeline, higher resolution (1 Day) = longer timeline
	let resolutionFactor = 1
	switch (resolution) {
		case '1 Day':
			resolutionFactor = 1.0 // Full scale
			break
		case '1 Week':
			resolutionFactor = 0.7 // 70% scale - more compact
			break
		case '2 Weeks':
			resolutionFactor = 0.4 // 40% scale - very compact
			break
	}

	// Calculate timeline inner width independently based on date range
	// Use a reasonable pixels per day (minimum 50px, maximum 200px per day)
	const minPixelsPerDay = 50
	const maxPixelsPerDay = 200
	// Calculate pixels per day based on viewport width to fit nicely
	const preferredPixelsPerDay =
		dateRange > 0 ? Math.max(minPixelsPerDay, Math.min(maxPixelsPerDay, viewportWidth / dateRange)) : 50
	// Apply resolution factor to make timeline shorter for lower resolutions
	const pixelsPerDay = preferredPixelsPerDay * resolutionFactor
	// Timeline inner width is calculated independently - can be wider than viewport
	const timelineWidth = dateRange * pixelsPerDay + 200

	// Calculate current date position
	// Always show current date marker - it's guaranteed to be in range
	const nowDay = moment().startOf('day')
	const minDateDay = moment(finalMinDate).startOf('day')
	const daysFromStart = Math.max(0, nowDay.diff(minDateDay, 'days'))
	const currentDatePosition = daysFromStart * pixelsPerDay + 100

	// getStatusIcon imported from shared utility

	useEffect(() => {
		const updateDimensions = () => {
			if (containerRef.current) {
				// Get the parent container width (not the timeline container itself)
				const container = containerRef.current
				const parent = container.parentElement
				if (parent) {
					const parentWidth = parent.offsetWidth || parent.clientWidth
					if (parentWidth > 0) {
						// Only update if significantly different to prevent feedback loops
						setContainerWidth(prev => {
							if (Math.abs(prev - parentWidth) > 5 || prev === 0) {
								if (!isInitialized) {
									setIsInitialized(true)
								}
								return parentWidth
							}
							return prev
						})
					}
				}
			}
			// Update scroll dimensions for button state
			if (scrollContainerRef.current) {
				setScrollWidth(scrollContainerRef.current.scrollWidth)
				setClientWidth(scrollContainerRef.current.clientWidth)
			}
		}

		// Use requestAnimationFrame to ensure DOM is ready
		const rafId = requestAnimationFrame(updateDimensions)
		// Also check after a short delay to catch any layout changes
		const timeoutId = setTimeout(updateDimensions, 100)

		window.addEventListener('resize', updateDimensions)
		return () => {
			cancelAnimationFrame(rafId)
			clearTimeout(timeoutId)
			window.removeEventListener('resize', updateDimensions)
		}
	}, [sortedTasks, isInitialized])

	// Set initial scroll position to show current date at the left edge
	useEffect(() => {
		if (scrollContainerRef.current && isInitialized && currentDatePosition > 0) {
			// Scroll to position current date at the left edge (accounting for padding)
			const scrollPosition = Math.max(0, currentDatePosition - 50)
			scrollContainerRef.current.scrollLeft = scrollPosition
			setScrollLeft(scrollPosition)
		}
	}, [currentDatePosition, isInitialized, timelineWidth, resolution])

	const handleScroll = () => {
		if (scrollContainerRef.current) {
			setScrollLeft(scrollContainerRef.current.scrollLeft)
		}
	}

	const scrollLeftHandler = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' })
		}
	}

	const scrollRightHandler = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' })
		}
	}

	const getTaskPosition = (task: Task) => {
		const taskDate = moment(task.completedBy)
		const daysFromStart = taskDate.diff(finalMinDate, 'days')
		return daysFromStart * pixelsPerDay + 100
	}

	// Effective render position for a task — equals base position, plus drift if
	// this task is currently being dragged. After drop, stays pinned at the drop
	// position via dropTargetDay until the underlying data catches up.
	const getRenderPosition = (task: Task): number => {
		const base = getTaskPosition(task)
		if (taskDrag && taskDrag.task._id === task._id) {
			if (taskDrag.dropTargetDay || taskDrag.hasMoved) {
				return taskDrag.startPosition + taskDrag.currentDeltaX
			}
		}
		return base
	}

	// Convert a pixel position on the timeline back to a noon-UTC ISO date string,
	// snapped to whole days. Inverse of getTaskPosition's math.
	const positionToISODate = (positionPx: number): string => {
		const dayOffset = Math.round((positionPx - 100) / pixelsPerDay)
		const d = moment(finalMinDate).startOf('day').add(dayOffset, 'days')
		return `${d.format('YYYY-MM-DD')}T12:00:00.000Z`
	}

	// Pretty label for the floating drag-tooltip ("Mon, Mar 5")
	const formatDragLabel = (positionPx: number): string => {
		const dayOffset = Math.round((positionPx - 100) / pixelsPerDay)
		return moment(finalMinDate).startOf('day').add(dayOffset, 'days').format('ddd, MMM D')
	}

	// ─── Pan handlers (drag whitespace to scroll back/forward in time) ─────────

	const handleScrollContainerMouseDown = (e: React.MouseEvent) => {
		if (!scrollContainerRef.current) return
		// Only left mouse button starts a pan
		if (e.button !== 0) return
		setPanState({
			startX: e.clientX,
			startScrollLeft: scrollContainerRef.current.scrollLeft,
		})
	}

	useEffect(() => {
		if (!panState) return
		const onMove = (e: MouseEvent) => {
			if (!scrollContainerRef.current) return
			const delta = e.clientX - panState.startX
			scrollContainerRef.current.scrollLeft = panState.startScrollLeft - delta
		}
		const onUp = () => setPanState(null)
		document.addEventListener('mousemove', onMove)
		document.addEventListener('mouseup', onUp)
		return () => {
			document.removeEventListener('mousemove', onMove)
			document.removeEventListener('mouseup', onUp)
		}
	}, [panState])

	// ─── Task drag handlers (mousedown on a task point → click vs reschedule) ──

	const handleTaskPointMouseDown = (e: React.MouseEvent, task: Task) => {
		// Stop propagation so this gesture doesn't also start a timeline pan
		e.stopPropagation()
		if (e.button !== 0) return
		// Completed tasks: don't allow drag-reschedule (matches Calendar behavior).
		// They still open the sheet on click via the click handler below.
		if (task.completed) return
		setTaskDrag({
			task,
			startX: e.clientX,
			startPosition: getTaskPosition(task),
			currentDeltaX: 0,
			hasMoved: false,
		})
	}

	useEffect(() => {
		if (!taskDrag) return
		const onMove = (e: MouseEvent) => {
			const deltaX = e.clientX - taskDrag.startX
			const hasMoved = taskDrag.hasMoved || Math.abs(deltaX) > DRAG_THRESHOLD_PX
			setTaskDrag(d => (d ? { ...d, currentDeltaX: deltaX, hasMoved } : null))
		}
		const onUp = (e: MouseEvent) => {
			const deltaX = e.clientX - taskDrag.startX
			const moved = taskDrag.hasMoved || Math.abs(deltaX) > DRAG_THRESHOLD_PX
			if (moved && onTaskDateChange) {
				const newPosition = taskDrag.startPosition + deltaX
				const newISO = positionToISODate(newPosition)
				const newDay = newISO.slice(0, 10)
				// Keep the task pinned at the dropped pixel position; clear after data
				// catches up (see effect below) or the safety timeout.
				setTaskDrag(d => (d ? { ...d, currentDeltaX: deltaX, hasMoved: true, dropTargetDay: newDay } : null))
				onTaskDateChange(taskDrag.task, newISO)
			} else {
				if (!moved && onTaskClick) {
					// Treat as a click on the task point — open the sheet
					onTaskClick(taskDrag.task)
				}
				setTaskDrag(null)
			}
		}
		document.addEventListener('mousemove', onMove)
		document.addEventListener('mouseup', onUp)
		return () => {
			document.removeEventListener('mousemove', onMove)
			document.removeEventListener('mouseup', onUp)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taskDrag])

	// After a successful drop (dropTargetDay set), keep the task pinned until the
	// parent's data update arrives. Compare the dragged task's current dateDue/completedBy
	// against the target day; clear the drag once they match.
	useEffect(() => {
		if (!taskDrag?.dropTargetDay) return
		const updated = (Array.isArray(tasks) ? tasks : []).find(t => t._id === taskDrag.task._id)
		if (!updated) {
			// Task disappeared (e.g., filtered out). Clear so the user isn't stuck.
			setTaskDrag(null)
			return
		}
		const updatedDay = moment(updated.completedBy).utc().format('YYYY-MM-DD')
		if (updatedDay === taskDrag.dropTargetDay) {
			setTaskDrag(null)
		}
	}, [tasks, taskDrag])

	// Safety timeout — if the dispatch never resolves (network failure, silent
	// rejection), don't pin the task forever. Snap back after 3 seconds so the
	// user sees the actual current state.
	useEffect(() => {
		if (!taskDrag?.dropTargetDay) return
		const t = setTimeout(() => setTaskDrag(null), 3000)
		return () => clearTimeout(t)
	}, [taskDrag?.dropTargetDay])

	// Determine if labels above the timeline should be upper or lower
	const getAboveLabelPositions = (tasks: Task[]): boolean[] => {
		const abovePositions: boolean[] = []
		let aboveIndex = 0

		for (let i = 0; i < tasks.length; i++) {
			if (i % 2 === 0) {
				abovePositions.push(aboveIndex % 2 === 1)
				aboveIndex++
			} else {
				abovePositions.push(true)
			}
		}

		return abovePositions
	}

	// Determine if labels below the timeline should be upper or lower
	const getBelowLabelPositions = (tasks: Task[]): boolean[] => {
		const belowPositions: boolean[] = []
		let belowIndex = 0

		for (let i = 0; i < tasks.length; i++) {
			if (i % 2 === 1) {
				belowPositions.push(belowIndex % 2 === 1)
				belowIndex++
			} else {
				belowPositions.push(true)
			}
		}

		return belowPositions
	}

	// Generate time scale ticks based on resolution
	const generateTimeScaleTicks = () => {
		const ticks: { position: number; label: string }[] = []
		if (!isNaN(dateRange) && dateRange >= 0 && isFinite(dateRange)) {
			let tickInterval: number
			let dateFormat: string

			// Determine tick interval and date format based on resolution
			switch (resolution) {
				case '1 Day':
					tickInterval = 1
					dateFormat = 'MMM DD'
					break
				case '1 Week':
					tickInterval = 7
					dateFormat = 'MMM DD'
					break
				case '2 Weeks':
					tickInterval = 14
					dateFormat = 'MMM DD'
					break
				default:
					tickInterval = 7
					dateFormat = 'MMM DD'
			}

			// Generate ticks based on the interval
			for (let i = 0; i <= dateRange; i += tickInterval) {
				const tickDate = moment(finalMinDate).add(i, 'days')
				const position = i * pixelsPerDay + 100
				ticks.push({
					position,
					label: tickDate.format(dateFormat),
				})
			}
			// Always show the last date
			if (ticks.length > 0 && ticks[ticks.length - 1].position < timelineWidth - 100) {
				ticks.push({
					position: timelineWidth - 100,
					label: finalMaxDate.format(dateFormat),
				})
			}
		}
		return ticks
	}

	const canScrollLeft = scrollLeft > 0
	const canScrollRight = scrollLeft < scrollWidth - clientWidth - 10

	if (sortedTasks.length === 0) {
		return (
			<div
				ref={containerRef}
				className="relative overflow-hidden bg-white border border-gray-200 rounded w-full"
				style={{ height: '680px', padding: '20px 50px 20px' }}
			>
				<div className="p-12 text-center text-gray-600 text-sm">
					No tasks with due dates to display on the timeline.
				</div>
			</div>
		)
	}

	return (
		<div
			ref={containerRef}
			className="relative overflow-hidden bg-white border border-gray-200 rounded w-full"
			style={{ height: '680px', padding: '20px 50px 20px', boxSizing: 'border-box' }}
		>
			{/* Scroll Left Button */}
			<button
				type="button"
				title="Scroll left"
				className="absolute left-2.5 z-10 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm cursor-pointer flex items-center justify-center transition-all hover:bg-gray-50 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
				style={{ top: '49%', transform: 'translateY(-50%)' }}
				onClick={scrollLeftHandler}
				disabled={!canScrollLeft}
			>
				<ChevronLeft className="w-6 h-6 text-[#0080fe]" />
			</button>

			{/* Scroll Right Button */}
			<button
				type="button"
				title="Scroll right"
				className="absolute right-2.5 z-10 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm cursor-pointer flex items-center justify-center transition-all hover:bg-gray-50 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
				style={{ top: '49%', transform: 'translateY(-50%)' }}
				onClick={scrollRightHandler}
				disabled={!canScrollRight}
			>
				<ChevronRight className="w-6 h-6 text-[#0080fe]" />
			</button>

			{/* Scrollable Timeline Container — mousedown here pans the timeline */}
			<div
				ref={scrollContainerRef}
				onScroll={handleScroll}
				onMouseDown={handleScrollContainerMouseDown}
				className={`relative overflow-x-auto overflow-y-visible select-none ${
					panState ? 'cursor-grabbing' : 'cursor-grab'
				}`}
				style={{
					width: `${viewportWidth}px`,
					maxWidth: '100%',
					height: '640px',
					scrollbarWidth: 'thin',
					boxSizing: 'border-box',
				}}
			>
				{/* Timeline Inner */}
				<div
					className="relative"
					style={{ width: `${timelineWidth}px`, minWidth: '100%', height: '620px' }}
				>
					{/* Timeline Line */}
					<div
						className="absolute left-0 right-0 h-0.5 bg-[#0080fe] z-[1]"
						style={{ top: '50%', transform: 'translateY(-50%)' }}
					/>

					{/* Current date marker */}
					<div
						title={`Today: ${moment().format('MM/DD/YY')}`}
						className="absolute w-5 h-5 rounded-full bg-[#ff9800] border-[3px] border-white z-[4]"
						style={{
							left: `${currentDatePosition}px`,
							top: '50%',
							transform: 'translate(-50%, -50%)',
							boxShadow: '0 2px 6px rgba(255, 152, 0, 0.5)',
						}}
					/>

					{(() => {
						// Pre-calculate positions for labels above and below to determine stagger
						const aboveLabelPositions = getAboveLabelPositions(sortedTasks)
						const belowLabelPositions = getBelowLabelPositions(sortedTasks)

						return sortedTasks.map((task, index) => {
							// Use getRenderPosition so the task drifts visually while it's being dragged.
							const position = getRenderPosition(task)
							// "Actively dragging" = mouse is held down and moving. After drop the
							// task is still pinned via dropTargetDay but we don't want the tooltip
							// or grab cursor effect to linger.
							const isDragging = !!(
								taskDrag &&
								taskDrag.task._id === task._id &&
								taskDrag.hasMoved &&
								!taskDrag.dropTargetDay
							)
							const canDragTask = !!onTaskDateChange && !task.completed
							const isAbove = index % 2 === 0
							// Determine if label should be upper or lower based on stagger pattern
							const isUpper = isAbove ? aboveLabelPositions[index] : belowLabelPositions[index]

							// Handle assignedTo which might be an object, string, or null
							const assignedTo =
								typeof task.assignedTo === 'object' && task.assignedTo !== null ? task.assignedTo : null
							const assignedToName = assignedTo?.name || 'Unassigned'
							const assignedToImage = assignedTo?.image
							const assignedToInitial = assignedToName.charAt(0).toUpperCase()

							// Get status icon
							const { icon: StatusIconComponent, color: statusColor } = getStatusIcon(task)

							// Calculate label positioning
							let labelTop: string | undefined
							let labelBottom: string | undefined
							let lineLength: number

							if (isAbove) {
								const distance = isUpper !== undefined && !isUpper ? LOWER_ABOVE_DISTANCE : UPPER_ABOVE_DISTANCE
								labelTop = `${distance}px`
								labelBottom = undefined
								lineLength = isUpper !== undefined && !isUpper ? LOWER_ABOVE_LINE_LENGTH : UPPER_ABOVE_LINE_LENGTH
							} else {
								const distance = isUpper !== undefined && !isUpper ? LOWER_BELOW_DISTANCE : UPPER_BELOW_DISTANCE
								labelTop = undefined
								labelBottom = `${distance}px`
								lineLength = isUpper !== undefined && !isUpper ? LOWER_BELOW_LINE_LENGTH : UPPER_BELOW_LINE_LENGTH
							}

							return (
								<React.Fragment key={task._id}>
									{/* Timeline Point — mousedown decides click vs drag-to-reschedule */}
									<div
										className={`absolute w-4 h-4 rounded-full border-[3px] border-white z-[3] transition-transform hover:scale-[1.3] ${
											canDragTask ? 'cursor-grab' : 'cursor-pointer'
										} ${isDragging ? '!cursor-grabbing scale-[1.3]' : ''}`}
										style={{
											left: `${position}px`,
											top: '50%',
											background: task.completed ? '#4caf50' : '#0080fe',
											boxShadow: isDragging
												? '0 4px 12px rgba(0, 128, 254, 0.6)'
												: '0 2px 4px rgba(0, 0, 0, 0.2)',
											transform: 'translate(-50%, -50%)',
										}}
										onMouseDown={e => handleTaskPointMouseDown(e, task)}
									/>

									{/* Drag tooltip — shows the date the task will land on */}
									{isDragging && (
										<div
											className="absolute z-[5] px-2 py-1 rounded bg-gray-900 text-white text-xs font-medium pointer-events-none whitespace-nowrap"
											style={{
												left: `${position}px`,
												top: 'calc(50% - 28px)',
												transform: 'translateX(-50%)',
											}}
										>
											{formatDragLabel(position)}
										</div>
									)}

									{/* Timeline Label */}
									<div
										className="absolute flex flex-col items-center gap-2 z-[2] w-fit"
										style={{
											left: `${position}px`,
											top: labelTop,
											bottom: labelBottom,
											transform: 'translateX(-50%)',
											opacity: isDragging ? 0.5 : 1,
											transition: isDragging ? 'none' : 'opacity 120ms ease',
										}}
									>
										{/* Label Line */}
										<div
											className="absolute left-1/2 -translate-x-1/2 w-0.5 bg-gray-300 z-[1]"
											style={
												isAbove
													? { top: '100%', height: `${lineLength}px` }
													: { bottom: '100%', height: `${lineLength}px` }
											}
										/>

										{/* Label Content — clicking the card opens the sheet (no drag from here) */}
										<div
											className="flex flex-col items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer transition-shadow hover:shadow-md"
											onMouseDown={e => e.stopPropagation()}
											onClick={() => onTaskClick && onTaskClick(task)}
										>
											<div className="flex items-center justify-between w-full gap-2">
												<Avatar className="h-8 w-8">
													<AvatarImage src={assignedToImage} alt={assignedToName} />
													<AvatarFallback>{assignedToInitial}</AvatarFallback>
												</Avatar>
												<div className="flex items-center justify-center text-xl" style={{ color: statusColor }}>
													<StatusIconComponent />
												</div>
											</div>
											<div className="text-xs font-semibold text-gray-700 text-center whitespace-nowrap w-fit">
												{task.taskName}
											</div>
										</div>
									</div>
								</React.Fragment>
							)
						})
					})()}

					{/* Time Scale */}
					<div
						className="absolute bottom-0 left-0 border-t border-gray-200 bg-[#0080fe] pt-1"
						style={{ minWidth: '100%', width: `${timelineWidth}px`, height: '30px' }}
					>
						{generateTimeScaleTicks().map((tick, index) => (
							<div
								key={index}
								className="absolute top-0 w-px h-full bg-gray-300"
								style={{ left: `${tick.position}px` }}
							>
								<span
									className="absolute left-1/2 -translate-x-1/2 text-[10px] text-white whitespace-nowrap"
									style={{ top: '8px' }}
								>
									{tick.label}
								</span>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Resolution Select */}
			<div className="absolute top-2.5 right-2.5 z-10 flex items-center">
				<select
					id="resolution-select"
					title="Timeline resolution"
					value={resolution}
					onChange={e => setResolution(e.target.value as '1 Day' | '1 Week' | '2 Weeks')}
					className="h-7 text-xs bg-white border border-gray-200 rounded px-2 pr-6 focus:outline-none focus:ring-1 focus:ring-primary appearance-none cursor-pointer"
					style={{
						backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
						backgroundRepeat: 'no-repeat',
						backgroundPosition: 'right 4px center',
					}}
				>
					<option value="1 Day">1 Day</option>
					<option value="1 Week">1 Week</option>
					<option value="2 Weeks">2 Weeks</option>
				</select>
			</div>
		</div>
	)
}

export default TasksTimeline
